import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../../../api";
import s from "./AiLogsPanel.module.css";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function groupBySession(logs) {
  const map = new Map();
  for (const log of logs) {
    if (!map.has(log.session_id)) {
      map.set(log.session_id, {
        session_id: log.session_id,
        user_email: log.user_email,
        user_id: log.user_id,
        origin: log.origin,
        messages: [],
        latest: log.created_at,
      });
    }
    const entry = map.get(log.session_id);
    entry.messages.push(log);
    if (log.created_at > entry.latest) entry.latest = log.created_at;
  }
  return Array.from(map.values());
}

function formatTime(iso) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const LIMIT = 100;

export default function AiLogsPanel() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterUserInput, setFilterUserInput] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [filterEmailInput, setFilterEmailInput] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterOrigin, setFilterOrigin] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedSession, setSelectedSession] = useState(null);

  const applyUserFilter = useCallback(() => {
    const val = filterUserInput.trim();
    if (val && !UUID_RE.test(val)) return;
    setFilterUser(val);
    setOffset(0);
    setSelectedSession(null);
  }, [filterUserInput]);

  const applyEmailFilter = useCallback(() => {
    setFilterEmail(filterEmailInput.trim());
    setOffset(0);
    setSelectedSession(null);
  }, [filterEmailInput]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (filterUser) params.set("user_id", filterUser);
      if (filterEmail) params.set("user_email", filterEmail);
      if (filterOrigin) params.set("origin", filterOrigin);
      params.set("limit", LIMIT);
      params.set("offset", offset);
      const res = await apiFetch(`/api/v1/admin/ai-logs?${params}`);
      if (!res.ok) throw new Error("Failed to fetch AI logs");
      const data = await res.json();
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterEmail, filterOrigin, offset]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const sessions = groupBySession(logs);

  return (
    <div className={s.panel}>
      <div className={s.header}>
        <h2 className={s.title}>AI Conversation Logs</h2>
        <div className={s.filters}>
          <input
            className={s.filterInput}
            placeholder="Filter by email (press Enter)..."
            value={filterEmailInput}
            onChange={(e) => setFilterEmailInput(e.target.value)}
            onBlur={applyEmailFilter}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyEmailFilter();
            }}
          />
          <input
            className={s.filterInput}
            placeholder="Filter by user ID (press Enter)..."
            value={filterUserInput}
            onChange={(e) => setFilterUserInput(e.target.value)}
            onBlur={applyUserFilter}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyUserFilter();
            }}
          />
          <select
            className={s.filterSelect}
            value={filterOrigin}
            onChange={(e) => {
              setFilterOrigin(e.target.value);
              setOffset(0);
              setSelectedSession(null);
            }}
          >
            <option value="">All origins</option>
            <option value="ask_ai">Ask AI</option>
            <option value="ai_tutor">AI Tutor</option>
            <option value="learning_board">Learning Board</option>
            <option value="vocabulary">Vocabulary</option>
            <option value="story">Story</option>
            <option value="listen_respond_english">Listen & Respond (English)</option>
            <option value="listen_respond_swedish">Listen & Respond (Swedish)</option>
            <option value="flashcard">Flashcard</option>
            <option value="role_play">Role Play</option>
            <option value="daily_practice">Daily Practice</option>
            <option value="daily_practice_word_review">Daily Practice — Word Review</option>
            <option value="daily_practice_role_play">Daily Practice — Role Play</option>
          </select>
          <button className={s.refreshBtn} onClick={fetchLogs}>
            Refresh
          </button>
        </div>
      </div>

      {loading && <div className={s.state}>Loading...</div>}
      {error && <div className={s.stateError}>{error}</div>}

      {!loading && !error && (
        <div className={s.body}>
          <div className={s.sessionList}>
            <div className={s.sessionCount}>
              {total} messages — {sessions.length} conversations
            </div>
            {sessions.map((sess) => (
              <button
                key={sess.session_id}
                className={`${s.sessionItem} ${
                  selectedSession?.session_id === sess.session_id
                    ? s.sessionActive
                    : ""
                }`}
                onClick={() => setSelectedSession(sess)}
              >
                <span className={s.sessionEmail}>{sess.user_email}</span>
                <span className={s.sessionMeta}>
                  <span className={s.originBadge} data-origin={sess.origin}>
                    {sess.origin}
                  </span>
                  <span className={s.sessionTime}>
                    {formatTime(sess.latest)}
                  </span>
                </span>
                <span className={s.sessionMsgCount}>
                  {sess.messages.length} msg
                  {sess.messages.length !== 1 ? "s" : ""}
                </span>
              </button>
            ))}
            {sessions.length === 0 && (
              <div className={s.state}>No conversations found</div>
            )}
            {total > LIMIT && (
              <div className={s.pagination}>
                <button
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
                >
                  Prev
                </button>
                <span>
                  {Math.floor(offset / LIMIT) + 1} /{" "}
                  {Math.ceil(total / LIMIT)}
                </span>
                <button
                  disabled={offset + LIMIT >= total}
                  onClick={() => setOffset((o) => o + LIMIT)}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          <div className={s.thread}>
            {!selectedSession && (
              <div className={s.threadEmpty}>
                Select a conversation to view messages
              </div>
            )}
            {selectedSession && (
              <>
                <div className={s.threadHeader}>
                  <div className={s.threadUser}>
                    {selectedSession.user_email}
                  </div>
                  <div className={s.threadMeta}>
                    session: <code>{selectedSession.session_id}</code>
                    {" · "}origin:{" "}
                    <strong>{selectedSession.origin}</strong>
                  </div>
                </div>
                <div className={s.messages}>
                  {[...selectedSession.messages].reverse().map((msg) => (
                    <MessageRow key={msg.id} msg={msg} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MessageRow({ msg }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`${s.msgRow} ${msg.is_rate_limited ? s.msgRateLimited : ""}`}
    >
      <div className={s.msgTime}>
        {formatTime(msg.created_at)}
        {msg.is_rate_limited && (
          <span className={s.rateLimitBadge}>rate-limited</span>
        )}
      </div>
      <div className={s.msgBubble} data-role="user">
        <span className={s.msgRole}>User</span>
        <p className={s.msgText}>{msg.user_message}</p>
      </div>
      <div className={s.msgBubble} data-role="ai">
        <span className={s.msgRole}>AI</span>
        <p className={s.msgText}>{msg.ai_response}</p>
      </div>
      {msg.words && (
        <div className={s.msgContext}>
          <span className={s.msgContextLabel}>Word context:</span>
          <code className={s.msgContextJson}>
            {JSON.stringify(msg.words, null, 2)}
          </code>
        </div>
      )}
      <button
        className={s.expandBtn}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? "Hide" : "Show"} full OpenAI payload
      </button>
      {expanded && (
        <pre className={s.jsonDump}>
          {JSON.stringify(
            {
              sent: msg.full_context_sent_to_ai,
              returned: msg.full_context_returned,
            },
            null,
            2
          )}
        </pre>
      )}
    </div>
  );
}
